import React, { useCallback, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import PixelTypeColor from "../../interfaces/cleaning-map/pixel-color-type";
import MapInfo from "../../interfaces/cleaning-map/map-info";
import ActiveZones from "../../interfaces/cleaning-map/active-zones";
import Polygon from "../../interfaces/cleaning-map/zones/polygon";
import Circle from "../../interfaces/cleaning-map/zones/circle";
import Line from "../../interfaces/cleaning-map/zones/line";

export const getPointInObstacleCoords = (x: number, y: number, primaryMapInfo: MapInfo, noRound?: boolean) => {
  const coordinateX = (x - primaryMapInfo!.originX) / primaryMapInfo!.resolution;
  const coordinateY = primaryMapInfo.height - (y - primaryMapInfo!.originY) / primaryMapInfo!.resolution;
  if (noRound) {
    return { coordinateX, coordinateY };
  }
  return { coordinateX: Math.round(coordinateX), coordinateY: Math.round(coordinateY) };
};

const ActiveZonesCanvasComponent: React.FC<{
  activeZones: ActiveZones | null;
  primaryMapInfo: MapInfo;
  noPadding?: boolean;
  onLoaded: () => void;
}> = ({ activeZones, primaryMapInfo, noPadding, onLoaded }) => {
  const theme = useTheme();

  const additionalComponentsRef = useRef<HTMLCanvasElement | null>(null);
  const backgroundCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const drawPolygon = useCallback(
    (ctx: CanvasRenderingContext2D, polygon: Polygon, color: string, filled: boolean) => {
      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = `rgba(${color}, ${filled ? 0.4 : 0})`;
      ctx.beginPath();
      polygon.points.forEach((point, index) => {
        const coordPoint = getPointInObstacleCoords(point.coordinateX, point.coordinateY, primaryMapInfo);
        if (index === 0) {
          ctx.moveTo(coordPoint.coordinateX, coordPoint.coordinateY);
        } else {
          ctx.lineTo(coordPoint.coordinateX, coordPoint.coordinateY);
        }
      });
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = `rgb(${color})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    },
    [primaryMapInfo]
  );

  const drawRoomLimitPolygon = useCallback(
    (ctx: CanvasRenderingContext2D, polygon: Polygon) => {
      ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
      const shiftPixels = parseInt(theme.spacing(2), 10); // increase the size of the fill to account for the pixel offset
      ctx.fillRect(0, 0, primaryMapInfo.width + 2 * shiftPixels, primaryMapInfo.height + 2 * shiftPixels);
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0, 0, 0, 1)";
      ctx.beginPath();
      polygon.points.forEach((point, index) => {
        const coordPoint = getPointInObstacleCoords(point.coordinateX, point.coordinateY, primaryMapInfo);
        if (index === 0) {
          ctx.moveTo(coordPoint.coordinateX + shiftPixels, coordPoint.coordinateY + shiftPixels);
        } else {
          ctx.lineTo(coordPoint.coordinateX + shiftPixels, coordPoint.coordinateY + shiftPixels);
        }
      });
      ctx.closePath();
      ctx.fill();
      ctx.globalCompositeOperation = "source-over";
    },
    [primaryMapInfo, theme]
  );

  const drawCircle = useCallback(
    (ctx: CanvasRenderingContext2D, circle: Circle, color: string) => {
      ctx.strokeStyle = `rgb(${color})`;
      ctx.beginPath();
      const centerPoint = getPointInObstacleCoords(
        circle.center.coordinateX,
        circle.center.coordinateY,
        primaryMapInfo
      );

      const radius = circle.radius / primaryMapInfo.resolution; // Convert radius from meters to pixels
      ctx.arc(centerPoint.coordinateX, centerPoint.coordinateY, radius, 0, 2 * Math.PI);
      ctx.stroke();
    },
    [primaryMapInfo]
  );

  const drawLine = useCallback(
    (ctx: CanvasRenderingContext2D, line: Line, color: string) => {
      ctx.strokeStyle = `rgb(${color})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      const startPoint = getPointInObstacleCoords(line.start.coordinateX, line.start.coordinateY, primaryMapInfo);
      const endPoint = getPointInObstacleCoords(line.end.coordinateX, line.end.coordinateY, primaryMapInfo);

      ctx.moveTo(startPoint.coordinateX, startPoint.coordinateY);
      ctx.lineTo(endPoint.coordinateX, endPoint.coordinateY);
      ctx.stroke();
    },
    [primaryMapInfo]
  );

  const drawActiveZones = useCallback(
    (ctx: CanvasRenderingContext2D, backgroundCtx: CanvasRenderingContext2D | null, activeZones: ActiveZones) => {
      // Draw polygons
      activeZones.subZones.forEach((polygon) => {
        drawPolygon(ctx, polygon, PixelTypeColor.SubZones, false);
      });

      activeZones.cleaningZones.forEach((polygon) => {
        drawPolygon(ctx, polygon, PixelTypeColor.ActiveRedZones, false);
      });

      // Draw circles
      activeZones.cleaningCircles.forEach((circle) => {
        drawCircle(ctx, circle, PixelTypeColor.ActiveRedZones);
      });

      activeZones.restrictedZones.forEach((polygon) => {
        drawPolygon(ctx, polygon, PixelTypeColor.ActiveRedZones, true);
      });

      // Draw pylons (lines)
      activeZones.pylons.forEach((line) => {
        drawLine(ctx, line, PixelTypeColor.ActiveRedZones);
      });

      activeZones.restrictedLines.forEach((line) => {
        drawLine(ctx, line, PixelTypeColor.ActiveRedZones);
      });

      if (activeZones.roomLimitZone != null && backgroundCtx != null) {
        drawRoomLimitPolygon(backgroundCtx, activeZones.roomLimitZone);
      }
    },
    [drawCircle, drawLine, drawPolygon, drawRoomLimitPolygon]
  );

  useEffect(() => {
    if (!additionalComponentsRef.current) return;
    const canvas = additionalComponentsRef.current;

    canvas.width = primaryMapInfo.width;
    canvas.height = primaryMapInfo.height;
    const context = canvas.getContext("2d");
    if (!context) return;

    let backgroundContext = null;
    if (backgroundCanvasRef.current) {
      const backgroundCanvas = backgroundCanvasRef.current;
      backgroundCanvas.width = primaryMapInfo.width + parseInt(theme.spacing(4), 10);
      backgroundCanvas.height = primaryMapInfo.height + parseInt(theme.spacing(4), 10);
      backgroundContext = backgroundCanvas.getContext("2d");
    }

    if (activeZones) {
      drawActiveZones(context, backgroundContext, activeZones);
    }
    onLoaded();
  }, [activeZones, drawActiveZones, onLoaded, primaryMapInfo.height, primaryMapInfo.width, theme]);

  return (
    <React.Fragment>
      <canvas
        style={{ position: "absolute", top: noPadding ? 0 : theme.spacing(2), left: noPadding ? 0 : theme.spacing(2) }}
        ref={additionalComponentsRef}
      />
      {activeZones?.roomLimitZone != null && (
        <canvas ref={backgroundCanvasRef} style={{ position: "absolute", top: 0, left: 0 }} />
      )}
    </React.Fragment>
  );
};

const ActiveZonesCanvas = React.memo(ActiveZonesCanvasComponent);

export default ActiveZonesCanvas;
